/**
 * Light, dark, or whatever the OS says. The resolved theme goes on `<html data-theme>`,
 * which is what the tokens and Tailwind's `dark:` variant key on. The choice is a
 * per-browser convenience, so storage failing (private mode) only means "system".
 */
export type ThemeChoice = "light" | "dark" | "system";
export type Theme = "light" | "dark";

const KEY = "uto.theme";
const QUERY = "(prefers-color-scheme: dark)";

export function readTheme(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

export function saveTheme(choice: ThemeChoice): void {
  try {
    if (choice === "system") {
      localStorage.removeItem(KEY);
    } else {
      localStorage.setItem(KEY, choice);
    }
  } catch {
    // Storage blocked: the choice lasts until the page reloads.
  }
}

export function resolveTheme(choice: ThemeChoice, systemDark: boolean): Theme {
  if (choice === "system") {
    return systemDark ? "dark" : "light";
  }
  return choice;
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(QUERY).matches;
}

let unwatch: (() => void) | null = null;

/** Put the resolved theme on the page; on "system", keep following the OS. */
export function applyTheme(choice: ThemeChoice): Theme {
  const theme = resolveTheme(choice, systemPrefersDark());
  document.documentElement.dataset.theme = theme;
  unwatch?.();
  unwatch = null;
  if (choice === "system" && typeof window.matchMedia === "function") {
    const media = window.matchMedia(QUERY);
    const follow = () => {
      document.documentElement.dataset.theme = media.matches ? "dark" : "light";
    };
    media.addEventListener("change", follow);
    unwatch = () => media.removeEventListener("change", follow);
  }
  return theme;
}
